// Layout grammar. Containers resolve to CSS grid through data attributes and a small set
// of custom properties whose values come from closed enums, never from agent strings.

export const GAPS = ['none', 'xs', 'sm', 'md', 'lg', 'xl'] as const
export type Gap = typeof GAPS[number]

export const SPLIT_RATIOS = ['1:1', '2:1', '1:2', '3:1', '1:3', '3:2', '2:3'] as const
export type SplitRatio = typeof SPLIT_RATIOS[number]

export const MIN_WIDTHS = ['xs', 'sm', 'md', 'lg'] as const
export type MinWidth = typeof MIN_WIDTHS[number]

export const ALIGNS = ['start', 'center', 'end', 'stretch'] as const
export type Align = typeof ALIGNS[number]

export const MAX_GRID_COLUMNS = 12

const MIN_WIDTH_PX: Record<MinWidth, number> = { xs: 160, sm: 220, md: 300, lg: 400 }

// ---------------------------------------------------------------------------
// Responsive. Breakpoints are minimum widths of the tool itself (a CSS container query on
// .max-root), not of the viewport: the same tool runs full screen, in a POS details column
// and on a phone. Values are mobile first: base applies everywhere, md from 768px up, etc.

export const BREAKPOINTS = ['sm', 'md', 'lg', 'xl'] as const
export type Breakpoint = typeof BREAKPOINTS[number]
export const BREAKPOINT_PX: Record<Breakpoint, number> = { sm: 480, md: 768, lg: 1100, xl: 1440 }

export type ResponsiveMap<T> = { base?: T } & Partial<Record<Breakpoint, T>>
export type Responsive<T> = T | ResponsiveMap<T>

export function isResponsiveMap (value: unknown): value is ResponsiveMap<unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

/** The value in effect at a container width, for lint and tests. */
export function responsiveAt<T> (value: Responsive<T> | undefined, width: number): T | undefined {
  if (!isResponsiveMap(value)) return value
  let out = value.base
  for (const bp of BREAKPOINTS) {
    if (width >= BREAKPOINT_PX[bp] && value[bp] !== undefined) out = value[bp]
  }
  return out
}

/** Child widths inside a row; fill takes the remaining space */
export const ITEM_WIDTHS = ['auto', 'fill', 'xs', 'sm', 'md', 'lg'] as const
export type ItemWidth = typeof ITEM_WIDTHS[number]
export const ITEM_WIDTH_PX: Record<Exclude<ItemWidth, 'auto' | 'fill'>, number> = { xs: 72, sm: 128, md: 200, lg: 320 }

export const ASIDE_WIDTHS = ['sm', 'md', 'lg'] as const
export type AsideWidth = typeof ASIDE_WIDTHS[number]
export const ASIDE_WIDTH_PX: Record<AsideWidth, number> = { sm: 240, md: 300, lg: 380 }

export const JUSTIFIES = ['start', 'center', 'end', 'between'] as const
export type Justify = typeof JUSTIFIES[number]

/** Data attributes every interface node may carry: row child width and visibility by width */
export function boxAttrs (node: { width?: unknown, hideBelow?: unknown, hideFrom?: unknown }): Record<string, string> {
  const out: Record<string, string> = {}
  if (typeof node.width === 'string' && (ITEM_WIDTHS as readonly string[]).includes(node.width) && node.width !== 'auto') out['data-max-width'] = node.width
  if (typeof node.hideBelow === 'string' && (BREAKPOINTS as readonly string[]).includes(node.hideBelow)) out['data-max-hide-below'] = node.hideBelow
  if (typeof node.hideFrom === 'string' && (BREAKPOINTS as readonly string[]).includes(node.hideFrom)) out['data-max-hide-from'] = node.hideFrom
  return out
}

export interface GridLayout {
  type: 'grid'
  columns?: Responsive<number> | 'auto'
  minWidth?: MinWidth
  gap?: Gap
  align?: Align
}

export interface StackLayout {
  type: 'stack'
  gap?: Gap
  align?: Align
  direction?: 'vertical' | 'horizontal'
  wrap?: boolean
}

export interface SplitLayout {
  type: 'split'
  ratio?: SplitRatio
  gap?: Gap
  align?: Align
  /** Stacks below this width; default md */
  collapseBelow?: Breakpoint | 'never'
}

export interface RowLayout {
  type: 'row'
  gap?: Gap
  align?: Align | 'baseline'
  justify?: Justify
  wrap?: boolean
  /** Turns into a vertical stack below this width */
  stackBelow?: Breakpoint
}

export interface SidebarLayout {
  type: 'sidebar'
  side?: 'start' | 'end'
  asideWidth?: AsideWidth
  gap?: Gap
  align?: Align
  collapseBelow?: Breakpoint | 'never'
  /** Where the aside goes once stacked; default last */
  stackAside?: 'first' | 'last'
}

export type LayoutContainer = GridLayout | StackLayout | SplitLayout | RowLayout | SidebarLayout

export interface ResolvedLayout {
  attrs: Record<string, string>
  style: Record<string, string>
}

function gapVar (gap: Gap | undefined, fallback: Gap): string {
  const value = gap && (GAPS as readonly string[]).includes(gap) ? gap : fallback
  return value === 'none' ? '0px' : `var(--max-gap-${value})`
}

function clampColumns (columns: unknown): number {
  const n = Math.round(Number(columns))
  return Number.isFinite(n) ? Math.min(MAX_GRID_COLUMNS, Math.max(1, n)) : 1
}

const isBreakpoint = (v: unknown): v is Breakpoint => typeof v === 'string' && (BREAKPOINTS as readonly string[]).includes(v)

function collapseAttr (value: unknown): string {
  return value === 'never' ? 'never' : isBreakpoint(value) ? value : 'md'
}

export function resolveLayout (node: LayoutContainer): ResolvedLayout {
  const align = node.align && ([...ALIGNS, 'baseline'] as readonly string[]).includes(node.align) ? node.align : 'stretch'
  if (node.type === 'grid') {
    const auto = node.columns === 'auto' || node.columns === undefined
    const minWidth = MIN_WIDTH_PX[node.minWidth ?? 'sm'] ?? MIN_WIDTH_PX.sm
    const style: Record<string, string> = {
      '--max-layout-gap': gapVar(node.gap, 'md'),
      '--max-layout-align': align,
      '--max-grid-min': minWidth + 'px'
    }
    const attrs: Record<string, string> = { 'data-max-layout': 'grid', 'data-max-grid': auto ? 'auto' : 'fixed' }
    if (!auto && isResponsiveMap(node.columns)) {
      // The author chose the columns per width, so the automatic narrowing stays off
      attrs['data-max-grid'] = 'responsive'
      style['--max-grid-cols'] = String(clampColumns(node.columns.base ?? 1))
      for (const bp of BREAKPOINTS) {
        if (node.columns[bp] === undefined) continue
        attrs[`data-max-cols-${bp}`] = ''
        style[`--max-grid-cols-${bp}`] = String(clampColumns(node.columns[bp]))
      }
    } else if (!auto) {
      style['--max-grid-cols'] = String(clampColumns(node.columns))
    }
    return { attrs, style }
  }
  if (node.type === 'split') {
    const ratio = node.ratio && (SPLIT_RATIOS as readonly string[]).includes(node.ratio) ? node.ratio : '1:1'
    const [a, b] = ratio.split(':')
    return {
      attrs: { 'data-max-layout': 'split', 'data-max-collapse': collapseAttr(node.collapseBelow) },
      style: {
        '--max-layout-gap': gapVar(node.gap, 'lg'),
        '--max-layout-align': align,
        '--max-split-a': a + 'fr',
        '--max-split-b': b + 'fr'
      }
    }
  }
  if (node.type === 'row') {
    const justify = node.justify && (JUSTIFIES as readonly string[]).includes(node.justify) ? node.justify : 'start'
    return {
      attrs: {
        'data-max-layout': 'row',
        ...(node.wrap === false ? {} : { 'data-max-wrap': 'true' }),
        ...(isBreakpoint(node.stackBelow) ? { 'data-max-stack-below': node.stackBelow } : {})
      },
      style: {
        '--max-layout-gap': gapVar(node.gap, 'sm'),
        '--max-layout-align': node.align && node.align !== 'stretch' ? (node.align === 'start' || node.align === 'end' ? 'flex-' + node.align : node.align) : 'center',
        '--max-layout-justify': justify === 'between' ? 'space-between' : justify === 'start' || justify === 'end' ? 'flex-' + justify : justify
      }
    }
  }
  if (node.type === 'sidebar') {
    const width = node.asideWidth && (ASIDE_WIDTHS as readonly string[]).includes(node.asideWidth) ? node.asideWidth : 'md'
    return {
      attrs: {
        'data-max-layout': 'sidebar',
        'data-max-side': node.side === 'start' ? 'start' : 'end',
        'data-max-collapse': collapseAttr(node.collapseBelow),
        'data-max-stack-aside': node.stackAside === 'first' ? 'first' : 'last'
      },
      style: {
        '--max-layout-gap': gapVar(node.gap, 'lg'),
        '--max-layout-align': align === 'stretch' ? 'start' : align,
        '--max-aside-width': ASIDE_WIDTH_PX[width] + 'px'
      }
    }
  }
  return {
    attrs: {
      'data-max-layout': 'stack',
      'data-max-direction': node.direction === 'horizontal' ? 'horizontal' : 'vertical',
      ...(node.wrap ? { 'data-max-wrap': 'true' } : {})
    },
    style: {
      '--max-layout-gap': gapVar(node.gap, 'md'),
      '--max-layout-align': align
    }
  }
}

/** Grid item span; 'full' spans every column. Collapses to full width in narrow containers via CSS. */
export function resolveSpan (span: unknown, parentColumns?: number | 'auto'): Record<string, string> {
  if (span === 'full') {
    return { 'grid-column': '1 / -1' }
  }
  if (span === undefined || span === null) {
    return {}
  }
  let n = clampColumns(span)
  if (typeof parentColumns === 'number') {
    n = Math.min(n, clampColumns(parentColumns))
  }
  return n > 1 ? { 'grid-column': `span ${n}` } : {}
}
